import { createContext, memo, ReactNode, useContext, useEffect, useRef } from 'react';

import styled from 'styled-components';

import { Spinner } from '@components/loading-spinner';
import { useEvent } from '@hooks/use-event';
import { OverrideLocaleContext } from '@locale/locale-context';
import { LocaleCode } from '@locale/locale-store';

import { createFormStore, FormStoreInstance } from './create-form-store';
import type { FormValidationMode } from './types';

const FormContext = createContext<
  | {
      store: FormStoreInstance;
      handleSubmitClick: (action?: string) => void;
    }
  | undefined
>(undefined);

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) throw new Error('Missing Form component');

  return context;
};

export type FormSubmitFn<T> =
  | ((values: T, action: string) => Promise<any>)
  | ((values: T, action: string) => void);

export type FormProviderProps<T = any> = {
  children: ReactNode;
  onSubmit: FormSubmitFn<T>;
  onSubmitSuccess?(data: ReturnType<FormProviderProps<T>['onSubmit']>): void;
  updateErrors?: Partial<Record<keyof T, string>>;
  updateValues?: Partial<T>;
  overrideErrors?: Partial<Record<keyof T, string>>;
  overrideValues?: Partial<T>;
  isDisabled?: boolean;
  store?: FormStoreInstance;
  validationMode?: FormValidationMode;

  // Fix the locale the form is displayed in instead of using the current UI locale.
  locale?: LocaleCode;
  hideSubmitSpinner?: boolean;
};

export const FormProvider = memo<FormProviderProps>(
  ({
    children,
    onSubmit,
    onSubmitSuccess,
    updateErrors,
    updateValues,
    overrideValues,
    overrideErrors,
    store: externalFormStore,
    locale,
    isDisabled = false,
    validationMode = 'onBlur',
    hideSubmitSpinner = false,
  }) => {
    const storeRef = useRef(externalFormStore ?? createFormStore({ validationMode }));
    const actions = storeRef.current.actions;
    const { formId } = storeRef.current.getState();

    useEffect(() => {
      actions.setDisabled(isDisabled);
    }, [isDisabled, actions]);

    useEffect(() => {
      updateErrors && actions.updateErrors(updateErrors);
    }, [updateErrors, actions]);

    useEffect(() => {
      updateValues && actions.updateValues(updateValues);
    }, [updateValues, actions]);

    useEffect(() => {
      overrideValues && actions.setValues(overrideValues);
    }, [overrideValues, actions]);

    useEffect(() => {
      overrideErrors && actions.setErrors(overrideErrors);
    }, [overrideErrors, actions]);

    const handleSubmitClick = useEvent(async (action: string = 'submit') =>
      actions.submit({ onSubmit, action, onSubmitSuccess }),
    );

    return (
      <OverrideLocaleContext locale={locale}>
        <FormContext.Provider value={{ store: storeRef.current, handleSubmitClick }}>
          <FormContainer id={formId} hideSubmitSpinner={hideSubmitSpinner}>
            {children}
          </FormContainer>
        </FormContext.Provider>
      </OverrideLocaleContext>
    );
  },
);

const FormContainer = memo<{ id: string; hideSubmitSpinner: boolean; children: ReactNode }>(
  ({ children, hideSubmitSpinner, ...rest }) => {
    const {
      store: { useIsSubmitDisabled, useFormResetCount, useIsSubmitting },
      handleSubmitClick: handleSubmit,
    } = useFormContext();
    const resetCount = useFormResetCount();
    const isSubmitting = useIsSubmitting();
    const isDisabled = useIsSubmitDisabled();

    return (
      <FormElement
        key={resetCount}
        onSubmit={(e) => {
          e.stopPropagation();
          e.preventDefault();

          if (!isDisabled) {
            handleSubmit('submit');
          }
        }}
        {...rest}
      >
        {isSubmitting && !hideSubmitSpinner && <Spinner />}
        {children}
      </FormElement>
    );
  },
);

const FormElement = styled.form({
  position: 'relative',
});
